import { CheckCircle2, AlertTriangle, ArrowRight, Compass } from "lucide-react";

type Status = "aligned" | "partial" | "contradiction" | "missing";

const STATUS_META: Record<Status, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
  aligned: { label: "Aligned", cls: "bg-primary/10 text-primary", Icon: CheckCircle2 },
  partial: { label: "Partial", cls: "bg-amber-500/10 text-amber-500", Icon: AlertTriangle },
  contradiction: { label: "Contradiction", cls: "bg-destructive/10 text-destructive", Icon: AlertTriangle },
  missing: { label: "Not built", cls: "bg-muted text-muted-foreground", Icon: Compass },
};

const ALIGNMENT: { area: string; status: Status; today: string; roadmap: string }[] = [
  {
    area: "Geographic scope",
    status: "aligned",
    today: "Alabama-only (Mobile County pilot, 67-county data ceiling). All stats, bills, partners restricted to AL.",
    roadmap: "v1.3 is explicitly Alabama-first: \"EMRKT is built for Alabama.\" Out-of-state ideas only inform, never define.",
  },
  {
    area: "Core primitive",
    status: "aligned",
    today: "Ideas Board + Projects (promote idea → project, with Coach + grant matching).",
    roadmap: "Ideas → Projects → Case studies → Lessons learned, all flowing into the Archive. Our current vocabulary matches.",
  },
  {
    area: "Audience language",
    status: "aligned",
    today: "\"Alabama communities\" is the public framing across the site. \"Member\" replaces \"shareholder\" in UI labels. DBE references only remain inside research articles and real credential names.",
    roadmap: "v1.3 speaks to Alabama communities broadly. DBE-led framing stays internal, never a public surface concept.",
  },
  {
    area: "Focus areas",
    status: "aligned",
    today: "5 pillars (Health & Wellness pink, others sage).",
    roadmap: "v1.3 doesn't enumerate pillars — focus areas are an editorial choice we own.",
  },
  {
    area: "Revenue model",
    status: "partial",
    today: "Pricing page + Services page exist; no live billing, no services intake queue, no engagement → archive pipeline.",
    roadmap: "Professional services (grant writing, project development, research, community analysis, opportunity mapping, site assessments, strategic planning) + membership. Every engagement feeds the archive.",
  },
  {
    area: "Lessons learned capture",
    status: "missing",
    today: "Projects close without a structured \"what we learned / what we'd do differently\" artifact.",
    roadmap: "Lessons learned is a first-class Archive object in v1.3 — \"success and failure are equally valuable sources of learning.\"",
  },
  {
    area: "The Archive (searchable knowledge library)",
    status: "aligned",
    today: "/archive is live with cross-object search across ideas, projects, case studies, curated posts, insights, legislation, and partners. Insights page links into it.",
    roadmap: "Phase 2 deliverable — \"searchable, interconnected knowledge library.\" Shipped ahead of schedule; ongoing work is depth per object type.",
  },
  {
    area: "AI assistance",
    status: "aligned",
    today: "Coach (project + ideas + assessments), grant matching, recommendations. Per-user daily caps live.",
    roadmap: "Phase 4 \"collaborative tools that transform ideas into successful projects\" — we're ahead of schedule.",
  },
  {
    area: "Knowledge platform (Phase 1)",
    status: "aligned",
    today: "Insights, Legislation Tracker, Grant Finder, Community News, Data Explorer, Partner Network.",
    roadmap: "Phase 1 — \"most trusted source of community ideas, research, funding opportunities, and success stories.\"",
  },
  {
    area: "Services as revenue engine",
    status: "missing",
    today: "Services landing page exists; no intake form, no engagement tracking, no pipeline from client work → archive contribution.",
    roadmap: "Grant writing, project development, research, community analysis, opportunity mapping, site assessments, strategic planning — each engagement contributes new knowledge back.",
  },
  {
    area: "Community contribution surface (Phase 3)",
    status: "partial",
    today: "Authenticated users can submit ideas, comment, save items. No structured \"share project update / lesson learned\" flow.",
    roadmap: "Phase 3 — \"enable communities to contribute projects, updates, insights, and lessons learned.\"",
  },
  {
    area: "Case studies & success stories",
    status: "aligned",
    today: "/archive/case-studies is live, backed by structured project lessons data and surfaced in the Insights rail.",
    roadmap: "Case studies and success stories are explicit Archive objects in v1.3 — Phase 1 deliverable. Shipped.",
  },
  {
    area: "Community profiles",
    status: "partial",
    today: "/communities live for Mobile County and Prichard with narrative profiles, live legislation counts, and case-study matches. 65 remaining AL counties not yet profiled.",
    roadmap: "\"Community profiles\" listed as an Archive object — connects local context to ideas and projects. Pilot shipped; scale to remaining counties as content allows.",
  },
];

const PIVOTS: { title: string; body: string; phase: string }[] = [
  {
    phase: "Now",
    title: "Stand up Services intake → Archive pipeline",
    body:
      "Services page is live but inert. Add an intake form (grant writing, project development, research, community analysis, opportunity mapping, site assessments, strategic planning) → admin queue. Define how each engagement contributes back to the archive (case study, lesson, profile update).",
  },
  {
    phase: "Next (60–120 days)",
    title: "Add Lessons Learned as a first-class object",
    body:
      "On every Project, add a structured \"What we learned / What we'd do differently / What others should know\" capture. v1.3 makes this the moral core: \"success and failure are equally valuable.\" Without it, projects close and knowledge evaporates.",
  },
  {
    phase: "Next",
    title: "Expand Community profiles beyond Mobile + Prichard",
    body:
      "Pilot profiles for Mobile County and Prichard are live at /communities. Next: extend to the next tier of priority counties (Birmingham/Jefferson, Montgomery, Selma/Dallas, Huntsville/Madison) with the same narrative + live data pattern.",
  },
  {
    phase: "Later (120+ days)",
    title: "Membership tiers → live billing",
    body:
      "v1.3 keeps membership simple: \"enhanced research, collaboration tools, project resources, specialized content.\" Wire Free / Member tiers to Stripe and gate the right surfaces — no need for the 4-tier ladder.",
  },
  {
    phase: "Later",
    title: "Phase 3 contribution surface",
    body:
      "Open structured contribution beyond ideas — let communities post project updates, lessons, and insights directly into the Archive with light moderation. Required for the \"living\" claim.",
  },
  {
    phase: "Later",
    title: "Phase 4 collaboration tools",
    body:
      "Beyond Coach: shared workspaces for org teams co-developing a project, with the project's archive trail (versions, decisions, lessons) preserved automatically as it progresses.",
  },
];

const PHASE_GROUPS = ["Now (next 30–60 days)", "Next (60–120 days)", "Later (120+ days)"];

const RoadmapTab = () => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold mb-1">Founder Roadmap Alignment</h2>
        <p className="text-sm text-muted-foreground">
          How the live product maps to <span className="font-mono">EMRKT Founder Roadmap v0.1.1</span> — contradictions and pivots to plan against.
        </p>
      </div>

      <div className="bg-card border border-border rounded-[0.875rem] p-5">
        <h3 className="font-display font-semibold text-sm mb-4">Alignment matrix</h3>
        <div className="space-y-3">
          {ALIGNMENT.map((row) => {
            const meta = STATUS_META[row.status];
            const Icon = meta.Icon;
            return (
              <div
                key={row.area}
                className="border border-border rounded-[0.625rem] p-4 hover:bg-muted/20 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h4 className="font-display font-semibold text-sm">{row.area}</h4>
                  <span
                    className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1 ${meta.cls}`}
                  >
                    <Icon className="h-3 w-3" />
                    {meta.label}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-muted-foreground uppercase tracking-wide text-[10px] mb-1">Today</div>
                    <div className="text-foreground">{row.today}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground uppercase tracking-wide text-[10px] mb-1">Roadmap</div>
                    <div className="text-foreground">{row.roadmap}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-display font-semibold text-sm">Pivots to plan against</h3>
        {PHASE_GROUPS.map((phase) => {
          const items = PIVOTS.filter((p) => p.phase === phase);
          if (items.length === 0) return null;
          return (
            <div
              key={phase}
              className="bg-card border border-border rounded-[0.875rem] p-5"
            >
              <div className="text-xs uppercase tracking-wide text-primary mb-3">{phase}</div>
              <ul className="space-y-3">
                {items.map((p) => (
                  <li key={p.title} className="flex gap-3">
                    <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <div className="font-display font-semibold text-sm">{p.title}</div>
                      <p className="text-xs text-muted-foreground mt-0.5">{p.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RoadmapTab;